import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, X } from 'lucide-react';
import type { TenantBillingNotice } from '../../api/billingNotice';
import { useAuthStore } from '../../store/authStore';
import { useUiFlagsStore } from '../uiFlags';
import {
  BILLING_CONTACT_EMAIL, dismiss as saveDismissal, isDismissed, noticeMessage, noticeTitle,
} from './billingNoticeCopy';

const TONE = {
  reminder: { bg: 'var(--color-warn-dim)', border: 'var(--color-warn-border)', icon: 'var(--color-warn)' },
  final: { bg: 'var(--color-status-red-dim)', border: 'var(--color-status-red-border)', icon: 'var(--color-status-red)' },
} as const;

/** The overdue-payment banner itself. It sits in the page flow above the
 *  app, so it pushes the layout down instead of covering anything. Used by
 *  the shell and by the platform console's live preview. */
export const BillingNoticeBanner: React.FC<{
  notice: TenantBillingNotice;
  /** Shown only for a reminder; a final notice cannot be hidden. */
  onDismiss?: () => void;
  /** Preview inside the platform console: rounded, no live-region role. */
  preview?: boolean;
}> = ({ notice, onDismiss, preview = false }) => {
  const { t, i18n } = useTranslation();
  const tone = TONE[notice.level];
  const Icon = notice.level === 'final' ? AlertTriangle : Info;
  const message = noticeMessage(t, notice, i18n.language || 'es');
  const [before, after] = message.split(BILLING_CONTACT_EMAIL);
  const canDismiss = notice.level === 'reminder' && !!onDismiss;

  return (
    <div
      data-testid="billing-notice-banner"
      data-level={notice.level}
      role={preview ? undefined : notice.level === 'final' ? 'alert' : 'status'}
      aria-label={noticeTitle(t, notice)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, flexShrink: 0,
        padding: '10px 16px', fontSize: 13, lineHeight: 1.45,
        backgroundColor: tone.bg, color: 'var(--color-text)',
        borderBottom: `1px solid ${tone.border}`,
        ...(preview ? { border: `1px solid ${tone.border}`, borderRadius: 6 } : {}),
      }}
    >
      <Icon size={16} aria-hidden="true" style={{ color: tone.icon, flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        <strong style={{ fontWeight: 600, marginRight: 6 }}>{noticeTitle(t, notice)}.</strong>
        <span>
          {before}
          {after !== undefined && (
            <>
              <a href={`mailto:${BILLING_CONTACT_EMAIL}`} style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline' }}>
                {BILLING_CONTACT_EMAIL}
              </a>
              {after}
            </>
          )}
        </span>
        {notice.note && (
          <div data-testid="billing-notice-note" style={{ marginTop: 4, color: 'var(--color-text-muted)' }}>
            {notice.note}
          </div>
        )}
      </div>
      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('shell.billingNotice.dismissAria', 'Ocultar el aviso hasta mañana')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
            border: '1px solid var(--color-border)', background: 'var(--color-surface)',
            color: 'var(--color-text)', borderRadius: 4, padding: '3px 8px',
            fontSize: 12, cursor: 'pointer',
          }}
        >
          <X size={12} aria-hidden="true" />
          {t('shell.billingNotice.dismiss', 'Ocultar por hoy')}
        </button>
      )}
    </div>
  );
};

/** The shell's banner: the active notice of the signed-in tenant, as read
 *  with the interface flags at startup. */
export const BillingNoticeHost: React.FC<{ now?: () => Date }> = ({ now = () => new Date() }) => {
  const notice = useUiFlagsStore(s => s.billingNotice);
  const tenantId = useUiFlagsStore(s => s.loadedFor);
  const userId = useAuthStore(s => s.user?.id) ?? '';
  const [, rerender] = useState(0);
  if (!notice || !tenantId) return null;
  const scope = `${tenantId}:${userId}`;
  if (isDismissed(scope, notice, now())) return null;
  const onDismiss = () => {
    saveDismissal(scope, notice, now());
    rerender(n => n + 1);
  };
  return <BillingNoticeBanner notice={notice} onDismiss={onDismiss} />;
};

export default BillingNoticeHost;
